import { useState } from "react";
import { Tooltip } from "antd";
import {
  ThunderboltOutlined, ShoppingOutlined, FireOutlined,
  DownloadOutlined, MailOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { fetchAiCredits } from "../../../services/customer360Service";
import { openInvoice } from "../../../services/invoiceService";
import SendInvoiceModal from "../../../invoices/SendInvoiceModal";
import {
  cardStyle, inr, num, dateTime, statusChip, chip, TONE, titleCase,
  StatTile, UsageBar, TileGrid, PanelHeader, PanelTable, PanelSkeleton,
  PanelError, usePanelData,
} from "./shared";

/**
 * The AI credit ledger.
 *
 * Two numbers matter here and they are easy to confuse: credits, which is what
 * the customer was sold, and rupees, which is what those calls actually cost us.
 * They are kept in separate columns rather than merged into one "amount".
 */
const AiCreditsTab = ({ userId, active, customerEmail = "" }) => {
  const p = usePanelData(fetchAiCredits, userId, active);
  const [sending, setSending] = useState(null);
  const [opening, setOpening] = useState(null);

  const download = async (row) => {
    setOpening(row.id);
    try {
      await openInvoice(row.receipt_download_url);
    } catch (err) {
      toast.error(err.message || "This receipt could not be opened");
    } finally {
      setOpening(null);
    }
  };

  if (p.loading && !p.data) return <PanelSkeleton tiles={3} />;
  if (p.error) return <PanelError error={p.error} onRetry={p.reload} />;

  const w = p.data?.wallet || {};

  const columns = [
    {
      title: "Date", dataIndex: "created_at", width: 165,
      render: (v) => <span style={{ fontSize: 12.5, color: "#475569", whiteSpace: "nowrap" }}>{dateTime(v)}</span>,
    },
    {
      title: "Type", dataIndex: "type", width: 100,
      render: (v) => (String(v).toLowerCase() === "credit"
        ? chip("Credit", TONE.green)
        : chip(titleCase(v), TONE.red)),
    },
    {
      title: "Credits", dataIndex: "amount", width: 110, align: "right",
      /* Signed by type, because the ledger stores a magnitude — an unsigned 500
         beside a debit row reads as an addition. */
      render: (v, r) => {
        const credit = String(r.type).toLowerCase() === "credit";
        return (
          <span style={{
            fontWeight: 700, fontVariantNumeric: "tabular-nums",
            color: credit ? "#16a34a" : "#dc2626",
          }}>
            {credit ? "+" : "−"}{num(Math.abs(Number(v) || 0))}
          </span>
        );
      },
    },
    {
      title: "Balance after", dataIndex: "balance_after", width: 125, align: "right",
      render: (v) => <span style={{ fontWeight: 600, color: "#0f172a", fontVariantNumeric: "tabular-nums" }}>{num(v)}</span>,
    },
    {
      title: "Source", dataIndex: "reference_type", width: 150,
      render: (v) => (v ? chip(titleCase(v), TONE.violet) : <span style={{ color: "#cbd5e1" }}>—</span>),
    },
    {
      title: "Description", dataIndex: "description", width: 260,
      render: (v) => (v
        ? <Tooltip title={v}><span style={{ fontSize: 12.5, color: "#64748b" }}>{v}</span></Tooltip>
        : <span style={{ color: "#cbd5e1" }}>—</span>),
    },
    {
      title: "Tokens", key: "tokens", width: 140, align: "right",
      /* In and out together: the pair is what a call cost, and splitting them
         across two columns doubles the width to say the same thing. */
      render: (_, r) => {
        const i = Number(r.tokens_input) || 0;
        const o = Number(r.tokens_output) || 0;
        if (!i && !o) return <span style={{ color: "#cbd5e1" }}>—</span>;
        return (
          <Tooltip title={`${num(i)} in · ${num(o)} out`}>
            <span style={{ fontSize: 12, color: "#475569", fontVariantNumeric: "tabular-nums" }}>
              {num(i)} <span style={{ color: "#cbd5e1" }}>/</span> {num(o)}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Actual cost", dataIndex: "actual_cost_inr", width: 120, align: "right",
      render: (v) => <span style={{ fontSize: 12.5, color: "#475569", fontVariantNumeric: "tabular-nums" }}>{inr(v)}</span>,
    },
    { title: "Status", dataIndex: "status", width: 110, render: (v) => statusChip(v) },
    {
      title: "Receipt", key: "receipt", width: 170, fixed: "right",
      /* Only a row the customer actually paid for has a receipt. A welcome
         bonus and every usage debit come back with `is_purchase: false` and
         null URLs — the server decides, so a new kind of grant needs nothing
         here. */
      render: (_, r) => {
        if (r.is_purchase !== true) return <span style={{ color: "#cbd5e1" }}>—</span>;
        const gst = r.gst_amount;
        const hasGst = gst !== null && gst !== undefined && gst !== "" && Number(gst) > 0;
        return (
          <div>
            <div style={{ display: "inline-flex", gap: 6 }}>
              <Tooltip title="Open the receipt">
                <button
                  onClick={() => download(r)}
                  disabled={opening === r.id || !r.receipt_download_url}
                  style={{
                    width: 30, height: 30, borderRadius: 8, border: "1px solid #ddd6fe",
                    background: "#f5f3ff", color: "#6366f1", cursor: "pointer",
                    display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12,
                  }}
                >
                  <DownloadOutlined spin={opening === r.id} />
                </button>
              </Tooltip>
              <Tooltip title="Email this receipt">
                <button
                  onClick={() => setSending({
                    invoice_number: r.invoice_number || `AI credits #${r.id}`,
                    title: r.description || titleCase(r.reference_type || "Recharge"),
                    /* The ledger counts credits, not rupees — the price lives
                       in the receipt. Printing this figure with a ₹ in front
                       would state a number the customer never paid. */
                    amountText: `${num(Math.abs(Number(r.amount) || 0))} credits`,
                    send_endpoint: r.receipt_send_endpoint,
                  })}
                  disabled={!r.receipt_send_endpoint}
                  style={{
                    width: 30, height: 30, borderRadius: 8, border: "1px solid #bfdbfe",
                    background: "#eff6ff", color: "#2563eb", cursor: "pointer",
                    display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12,
                  }}
                >
                  <MailOutlined />
                </button>
              </Tooltip>
            </div>
            {/* The tax sits here rather than under the credit count, so the
                one cell that speaks rupees is the one about the receipt. */}
            {hasGst && (
              <div style={{ fontSize: 11, color: "#b45309", marginTop: 3, fontVariantNumeric: "tabular-nums" }}>
                GST {inr(gst)}{r.gst_percent ? ` (${r.gst_percent}%)` : ""}
              </div>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <TileGrid>
        <StatTile
          icon={<ThunderboltOutlined />} label="Credit balance" value={num(w.balance)}
          color="#7c3aed" bg="#f5f3ff"
        >
          <UsageBar used={w.total_credits_used} total={w.total_credits_purchased} color="#7c3aed" />
        </StatTile>
        <StatTile
          icon={<ShoppingOutlined />} label="Purchased" value={num(w.total_credits_purchased)}
          color="#2563eb" bg="#eff6ff"
        />
        <StatTile
          icon={<FireOutlined />} label="Used" value={num(w.total_credits_used)}
          color="#ea580c" bg="#fff7ed"
        />
      </TileGrid>

      <div style={cardStyle}>
        <PanelHeader
          icon={<ThunderboltOutlined />}
          title="AI credit ledger"
          subtitle="Credits bought, and what each call consumed"
          iconBg="linear-gradient(135deg,#7c3aed,#6366f1)"
        />
        <PanelTable
          columns={columns}
          rows={p.rows}
          loading={p.loading}
          page={p.page}
          total={p.total}
          perPage={p.perPage}
          onPage={p.goToPage}
          empty="No AI credit activity on this account yet."
          scrollX={1450}
        />
      </div>

      <SendInvoiceModal
        open={Boolean(sending)}
        invoice={sending}
        userId={userId}
        defaultEmail={customerEmail}
        onClose={() => setSending(null)}
        onSent={p.reload}
      />
    </>
  );
};

export default AiCreditsTab;
